// =============================================================================
// io/sync/saveJournalRecap.ts — the EOD recap write.
// -----------------------------------------------------------------------------
// The first journal write in this client, and deliberately the NARROWEST one that
// is useful: the six recap prose fields plus one boolean.
//
// ── 🚨 WHY IT IS THIS NARROW, AND NOT "SAVE THE ENTRY" ──
//
// D61 established that an editor must have READ every field it renders. The
// journal takes that one level further, and the reason is not the read — it is
// that the TYPED MODEL IS LOSSY. `normalisePlan` coerces two things it cannot
// round-trip:
//
//   selfAssessment.mood / focus / energy / stress — NUMBERS 1–5 in the old
//     client, `str()`-coerced to '' on read
//   hypotheses[].patterns — `{id, name}` OBJECTS in the old client, filtered to
//     `string[]` on read
//
// Neither survives in `plan.extra`, because both keys are in `normalisePlan`'s
// known set. And `SessionContract` models 4 of the 9 keys the old client stores —
// dropping `lockedAt`, `dllHitAt`, `dllAcknowledgedAt`, `targetHitAt`,
// `targetAcknowledgedAt` and `manualPnl`, one of which is the only input to a real
// analytics flag.
//
// So serialising a `JournalEntry` back to a row would silently destroy four
// self-assessment ratings, every hypothesis→pattern link, and six contract
// timestamps — on surfaces that never displayed any of them. A hydration read does
// not save you here: the loss happens in the TYPE, after a complete read.
//
// The fix is not a better serialiser. It is to never send a column this module
// does not own.
//
// ── WHY OMITTING A COLUMN IS SAFE (verified, not assumed) ──
//
// A PostgREST upsert becomes `INSERT … ON CONFLICT DO UPDATE SET col = EXCLUDED.col`
// for the columns PRESENT in the payload, so an omitted column is left alone on an
// existing row and takes its DDL default on a new one.
//
// That was the one claim the research could not settle from source, and it is
// settled on LIVE DATA instead: the old client stopped writing `eod_mood` /
// `eod_energy` in its Phase 2 audit, and rows from 2026-05-09..11 still read
// `eod_mood = 'Sick'` today after months of continued saves. Omitted columns
// survive.
//
// ⚠ THE KEY SET MUST BE FIXED. supabase-js unions the keys of an ARRAY payload
// into a single `columns=` parameter (`PostgrestQueryBuilder`), so rows with
// differing shapes get the union — and a row missing a unioned key is written as a
// default. This module sends ONE object with a constant key set, which is also
// exactly what `_journalToDb` does and why.
//
// ── UPSERT, NOT UPDATE — the opposite call from saveAnnotation ──
//
// `saveAnnotation` argues UPDATE-not-upsert because the trade row exists and an
// upsert on a mistyped id would insert a phantom. For a journal DATE the row very
// often does NOT exist: the first save of a new trading day creates it. Copying
// that reasoning verbatim would make every new day's first save fail with "matched
// no rows".
//
// The flip side: with `resolution=merge-duplicates` every payload object either
// inserts or updates, so `return=representation` yields exactly one row. An empty
// result here is a genuine failure, not a normal case.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import { EOD_SWAP_MARKER } from '../../domain/journal';
import { reportFailureAs } from '../diagnostics/errorLog';

/**
 * The six prose fields, and nothing else.
 *
 * ⚠ `mood` and `energy` are ABSENT ON PURPOSE. The new client's reader models
 * them, and the Journal tab even displays them — but the old client removed both
 * from its writer in a Phase 2 audit and no longer reads them either. Writing them
 * would make this client the sole writer of two orphan columns, and writing them
 * from a form that does not collect them would blank the legacy values that are
 * demonstrably still there (`'Sick'`, `'Calm'`, May 2026).
 */
export interface RecapPatch {
  whatWorked: string;
  whatBroke: string;
  /** ⚠ Read the header. Corrected orientation — see `booleans` below. */
  mistakes: string;
  /** ⚠ Read the header. Corrected orientation — see `booleans` below. */
  wins: string;
  lessons: string;
  tomorrowFocus: string;
}

export interface SaveRecapResult {
  /** The row's new `updated_at`, for advancing the stale-write baseline. */
  updatedAt: string | null;
  error: string | null;
  /** True when the guard held the write back — the entry must stay dirty. */
  heldStale: boolean;
}

/** Every column this module writes. Fixed, and asserted by a test. */
export const RECAP_COLUMNS = [
  'user_id', 'date',
  'eod_what_worked', 'eod_what_broke', 'eod_mistakes', 'eod_wins',
  'eod_lessons', 'eod_tomorrow_focus',
  'booleans',
] as const;

/**
 * Build the row.
 *
 * ── 🚨 `eod_mistakes`, `eod_wins` AND `booleans` ARE ONE ATOMIC WRITE ──
 *
 * The two columns are stored INVERTED on disk for any row the old client has not
 * re-saved since its fix (D60). The read un-inverts them AND stamps
 * `booleans._eodRecapSwapMigrated = true` in memory (`applyEodSwap`). The marker on
 * disk does not mean "swap me" — it means "this row is already correct".
 *
 * So the write is a straight pass-through of what is in memory, with NO
 * re-inversion, and the marker must travel in the same statement:
 *
 *   corrected text + marker  → both clients read it back correctly. ✅
 *   corrected text, NO marker → the next load in EITHER client swaps correct prose,
 *                               then persists the inversion WITH the marker set —
 *                               after which no swap ever fires again and the
 *                               corruption is indistinguishable from real data. ☠
 *   marker only               → the row stays inverted forever. ☠
 *
 * The marker is set here rather than trusted from the caller. It is already true
 * on every entry the reader produced, so this is belt-and-braces — but the cost of
 * it being false is a trader's wins filed under "what broke", permanently, and one
 * line is cheap insurance against a caller that reconstructs `booleans`.
 */
export function recapRow(
  date: string,
  patch: RecapPatch,
  booleans: Record<string, unknown>,
  userId: string,
): Record<string, unknown> {
  return {
    user_id: userId,
    date,
    eod_what_worked: patch.whatWorked,
    eod_what_broke: patch.whatBroke,
    eod_mistakes: patch.mistakes,
    eod_wins: patch.wins,
    eod_lessons: patch.lessons,
    eod_tomorrow_focus: patch.tomorrowFocus,
    booleans: { ...booleans, [EOD_SWAP_MARKER]: true },
  };
}

export interface SaveRecapArgs {
  sb: SupabaseClient;
  userId: string;
  date: string;
  patch: RecapPatch;
  /** The entry's booleans AS READ — passed through, never reconstructed. */
  booleans: Record<string, unknown>;
  /**
   * The `updated_at` this client last saw for the row, or null for a row that did
   * not exist at load. The stale check needs both this and the cloud's current
   * value; a null baseline means "we never loaded it", which is not a conflict.
   */
  baselineUpdatedAt: string | null;
}

/**
 * Save one day's recap.
 *
 * ⚠ NO DELETE PATH, EVER. The old client can delete journal rows — its sync diff
 * emits a `deletedIds` set for any date present in the snapshot and absent from
 * live state — but that is a whole-state diff, and nothing in a single-entry save
 * needs it. More to the point, this client's `isJournalEntryEmpty` disagrees with
 * the old client's in the DESTRUCTIVE direction: it calls an entry with voice
 * recordings or a completed readiness gate empty. Wiring it to a delete would
 * remove rows the old client considers full.
 */
export async function saveJournalRecap(args: SaveRecapArgs): Promise<SaveRecapResult> {
  const { sb, userId, date, patch, booleans, baselineUpdatedAt } = args;
  const row = recapRow(date, patch, booleans, userId);

  // ── the stale-write check ────────────────────────────────────────────────────
  // ⚠ FAIL-OPEN, matching guard.ts throughout: if the check cannot be performed
  // the write goes ahead. A guard that blocked saves on a transient network error
  // would lose real work, which is worse than the race it prevents.
  if (baselineUpdatedAt) {
    const { data, error } = await sb
      .from('journal_entries')
      .select('date, updated_at')
      .eq('user_id', userId)
      .eq('date', date);

    if (!error && Array.isArray(data) && data.length === 1) {
      const cloud = (data[0] as unknown as Record<string, unknown>)['updated_at'];
      if (typeof cloud === 'string'
        && new Date(cloud).getTime() > new Date(baselineUpdatedAt).getTime()) {
        // ⚠ Held, NOT written, and the caller is told so it can keep the entry
        // dirty. Reporting "saved" here is the two-tab loss bug from the other
        // end: detected, warned about, and then thrown away anyway.
        return {
          updatedAt: null,
          heldStale: true,
          error:
            `This day was changed somewhere else after you loaded it — probably the ` +
            `other client or another tab. Nothing was saved, so neither version is ` +
            `lost. Reload to see the newer copy, then re-apply your edit.`,
        };
      }
    }
  }

  // ⚠ `onConflict` NAMED EXPLICITLY, and the target is the composite PRIMARY KEY
  // `user_id,date` — NOT `date`, even though `date` is the key everywhere else in
  // the old client (the diff key, the guard key, the delete filter). The old
  // client names it too. Omitting it makes the target PostgREST's inference, which
  // is a question this write does not need to have.
  const { data, error } = await sb
    .from('journal_entries')
    .upsert(row, { onConflict: 'user_id,date' })
    // ⚠ A CORRECTNESS REQUIREMENT, not a convenience: without it `data` is null,
    // so the baseline cannot advance and the row is unguarded for the rest of the
    // session — and there is no way to confirm the write landed at all.
    .select('date, updated_at');

  if (error) {
    return {
      updatedAt: null,
      heldStale: false,
      error: reportFailureAs('save recap', error, `Save failed: ${error.message}`),
    };
  }

  // With `resolution=merge-duplicates` every payload object either inserts or
  // updates, so exactly one row comes back. Zero is a real failure here — unlike
  // an UPDATE, where zero legitimately means "no such row".
  if (!Array.isArray(data) || data.length !== 1) {
    return {
      updatedAt: null,
      heldStale: false,
      error:
        `The save returned ${Array.isArray(data) ? data.length : 'no'} rows instead of one, ` +
        `so it cannot be confirmed. Nothing here is known to have been written — ` +
        `reload before editing again.`,
    };
  }

  const ts = (data[0] as unknown as Record<string, unknown>)['updated_at'];
  return {
    updatedAt: typeof ts === 'string' ? ts : null,
    heldStale: false,
    error: null,
  };
}
